// =============================================================================
// Undo/Redo History Manager
// =============================================================================

import { MAX_HISTORY_STATES } from './constants';

/**
 * Generic undo/redo history manager.
 * Uses deep-clone snapshots for simplicity and correctness.
 */
export class HistoryManager<T> {
  private states: T[];
  private index: number;
  private maxStates: number;

  constructor(initialState: T, maxStates: number = MAX_HISTORY_STATES) {
    this.states = [structuredClone(initialState)];
    this.index = 0;
    this.maxStates = maxStates;
  }

  /** Push a new state, discarding any future states (redo history). */
  push(state: T): void {
    // Discard future states
    this.states = this.states.slice(0, this.index + 1);
    this.states.push(structuredClone(state));

    // Trim to max size
    if (this.states.length > this.maxStates) {
      this.states.shift();
    } else {
      this.index++;
    }
  }

  /** Undo: go back one state. Returns the state or null if at beginning. */
  undo(): T | null {
    if (!this.canUndo()) return null;
    this.index--;
    return structuredClone(this.states[this.index]);
  }

  /** Redo: go forward one state. Returns the state or null if at end. */
  redo(): T | null {
    if (!this.canRedo()) return null;
    this.index++;
    return structuredClone(this.states[this.index]);
  }

  canUndo(): boolean {
    return this.index > 0;
  }

  canRedo(): boolean {
    return this.index < this.states.length - 1;
  }

  /** Get current state (clone). */
  current(): T {
    return structuredClone(this.states[this.index]);
  }

  /** Reset history with a new initial state. */
  reset(state: T): void {
    this.states = [structuredClone(state)];
    this.index = 0;
  }

  /** Get history length. */
  get length(): number {
    return this.states.length;
  }

  /** Get current index. */
  get currentIndex(): number {
    return this.index;
  }
}
